import clsx from 'clsx';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtomValue } from 'jotai';

import { Button } from '@flintmint/web-shared/components/button';
import { Loader } from '@flintmint/web-shared/components/loader';
import { Popover } from '@flintmint/web-shared/components/popover';

import { pluralize } from '@flintmint/core-shared/utils';

import { formatJoules } from '@/features/billing/billing-format';
import { useAppSettings } from '@/hooks/use-app-settings';
import { useUserAccount } from '@/hooks/use-user-account';
import { appRoutes } from '@/constants/routes';
import { graceAllowanceUsedUpPeriodAtom } from '@/state';

import { BatteryIcon, RechargeIcon } from './battery-icon';
import {
  type EnergyAction,
  type EnergyAmount,
  type EnergyTone,
  type RechargeStatus,
  batteryLevel,
  chipAmount,
  chipTone,
  energyTone,
  getEnergyView,
  isGraceAllowanceUsedUp,
  rechargeStatus,
  rechargeTone,
} from './energy-view';
import { energyActionLabels, useEnergyAction } from './use-energy-action';

import styles from './energy-popover.module.scss';

/**
 * The composer's battery chip; opens the Energy the account is spending and its Joule balance, a renewal's used-up
 * grace allowance, or a comped account's unlimited Energy. Hidden while loading.
 */
export const EnergyPopover = () => {
  const { account, loading } = useUserAccount();
  const quotaPercentUsedWarning = useAppSettings()?.quotaPercentUsedWarning;
  const refusedGracePeriodStart = useAtomValue(graceAllowanceUsedUpPeriodAtom);
  if (loading || !account) return null;

  const view = getEnergyView(account);
  // A loaded account has no Energy view only when comped.
  if (!view) {
    return (
      <EnergyChip
        label="Energy, unlimited"
        icon={<BatteryIcon level="unlimited" className={styles.unlimited} />}
        rows={
          <div className={styles.header}>
            <BatteryIcon level="unlimited" size={20} className={styles.unlimited} />
            <span className={styles.label}>Monthly Energy</span>
            <span className={styles.percent}>Unlimited</span>
          </div>
        }
      />
    );
  }

  // The grace allowance's share of Monthly Energy is not on the account, so no meter or Joule rows: only the stop.
  if (isGraceAllowanceUsedUp(account, refusedGracePeriodStart)) {
    return (
      <EnergyChip
        label="Energy, grace allowance used up"
        icon={
          <>
            <BatteryIcon level={0} className={styles.danger} />
            <EnergyDot />
          </>
        }
        rows={
          <>
            <div className={styles.header}>
              <BatteryIcon level={0} size={20} className={styles.calm} />
              <span className={styles.label}>{view.label}</span>
            </div>
            <p className={styles.notice}>Your grace allowance is used up. Update your payment method to continue using AI.</p>
          </>
        }
        action={view.action}
      />
    );
  }

  const tone = energyTone(account, view, quotaPercentUsedWarning);
  const chip = chipAmount(account, view);
  const chipLevel = batteryLevel(chip.percentLeft);
  const recharge = rechargeStatus(account, view);
  const chipColor = chipTone(account, view, quotaPercentUsedWarning);
  const rechargeColor = rechargeTone(account, view, quotaPercentUsedWarning);

  return (
    <EnergyChip
      label={`Energy, ${chip.percentLeft}% left`}
      icon={
        <>
          {recharge === 'In use' ? (
            <BatteryIcon level="charging" className={styles[chipColor === 'danger' ? 'danger' : 'rechargeInUse']} />
          ) : (
            <BatteryIcon level={chipLevel} className={styles[chipColor]} />
          )}
          {chipLevel === 0 && <EnergyDot />}
        </>
      }
      rows={
        <>
          <EnergyBalance
            icon={
              <BatteryIcon
                level={batteryLevel(view.percentLeft)}
                size={20}
                className={styles[view.percentLeft === 0 ? 'calm' : tone]}
              />
            }
            label={view.label}
            amount={view}
            tone={tone}
            value={`${view.percentLeft}% left`}
            detail={formatJoules(view.joulesLeft, view.joulesTotal, view.suffix)}
          />
          {view.recharge && (
            <EnergyBalance
              icon={
                <RechargeIcon
                  size={20}
                  className={clsx({
                    [styles[rechargeColor]]: rechargeColor !== 'calm',
                    [styles.rechargeInUse]: rechargeColor === 'calm' && recharge === 'In use',
                    [styles.calm]: rechargeColor === 'calm' && recharge !== 'In use',
                  })}
                />
              }
              label="Recharge"
              amount={view.recharge}
              tone={rechargeColor}
              value={pluralize(view.recharge.joulesLeft, 'Joule')}
              status={recharge}
            />
          )}
        </>
      }
      action={view.action}
    />
  );
};

type EnergyChipProps = { label: string; icon: ReactNode; rows: ReactNode; action?: EnergyAction };

const EnergyChip = ({ label, icon, rows, action }: EnergyChipProps) => (
  <Popover
    placement="top"
    width={260}
    offset={[0, 8]}
    arrow
    subtleShadow
    cornerRadius="default"
    content={({ close }) => (
      <div className={styles.card}>
        {rows}
        <EnergyActions action={action} close={close} />
      </div>
    )}
  >
    <button type="button" aria-label={label} data-testid="energy-chip" className={styles.chip}>
      {icon}
    </button>
  </Popover>
);

const EnergyDot = () => <span className={styles.dot} data-testid="energy-dot" aria-hidden="true" />;

type EnergyBalanceProps = {
  icon: ReactNode;
  label: string;
  amount: EnergyAmount;
  tone: EnergyTone;
  value: string;
  detail?: string;
  status?: RechargeStatus;
};

const EnergyBalance = ({ icon, label, amount, tone, value, detail, status }: EnergyBalanceProps) => (
  <div className={styles.balance}>
    <div className={styles.header}>
      {icon}
      <span className={styles.label}>
        {label}
        {status && (
          <span className={styles.status}>
            {' '}
            {'\u2022'} {status}
          </span>
        )}
      </span>
      <span className={styles.percent}>{value}</span>
    </div>
    <Loader
      type="progress"
      progress={amount.percentLeft}
      color={tone === 'calm' ? 'hero' : tone}
      glow={tone !== 'calm' || status === 'In use'}
      thin
      className={clsx(styles.meter, {
        [styles.rechargeReady]: tone === 'calm' && status === 'Ready',
        [styles.rechargeInUse]: tone === 'calm' && status === 'In use',
      })}
    />
    {detail && <div className={styles.joules}>{detail}</div>}
  </div>
);

/**
 * The view's action, then Manage. Every button closes the popover except `updatePayment`, which stays open while the
 * Customer Portal session opens and then leaves the page.
 */
const EnergyActions = ({ action, close }: { action?: EnergyAction; close: () => void }) => {
  const navigate = useNavigate();
  const { runEnergyAction, opening } = useEnergyAction();

  return (
    <div className={styles.actions}>
      {action && (
        <Button
          color="light"
          cornerRadius="smooth"
          fullWidth
          disabled={opening}
          onClick={() => {
            if (action !== 'updatePayment') close();
            runEnergyAction(action);
          }}
        >
          {energyActionLabels[action]}
        </Button>
      )}
      <Button
        appearance="clear"
        color="grey"
        size="big"
        cornerRadius="smooth"
        fullWidth
        onClick={() => {
          close();
          void navigate(appRoutes.settingsBilling);
        }}
      >
        Manage
      </Button>
    </div>
  );
};
